import { z } from 'zod';

import { PlaylistKind } from '../catalog';
import { EntityId, I18nText, Page, PageQuery } from '../primitives';
import { SoftDelete, TextFilter, Timestamps } from './common';
import { SongStatus } from './songs';

export const PublishStatus = z.enum(['draft', 'published']);
export type PublishStatus = z.infer<typeof PublishStatus>;

/**
 * `playlists` row for the console (docs/02 §1, Figma CMS-06). Ids are `EntityId`: CMS-created
 * playlists get ULIDs, the seeded ones are slugs (`pl_placeholder_hits`).
 */
export const CmsPlaylistDTO = z
  .object({
    id: EntityId,
    kind: PlaylistKind,
    name: I18nText,
    description: z.string().nullable(),
    imagePath: z.string().nullable(),
    imageUrl: z.string().nullable(),
    artistId: EntityId.nullable(),
    status: PublishStatus,
    /** Songs assigned to the list (every row of `playlist_songs`). */
    total: z.number().int().nonnegative(),
    /** How many of those are published — what `GET /playlists/:id` will actually serve. */
    publishedTotal: z.number().int().nonnegative(),
  })
  .merge(Timestamps)
  .merge(SoftDelete);
export type CmsPlaylistDTO = z.infer<typeof CmsPlaylistDTO>;

/** `GET /cms/playlists` */
export const CmsPlaylistsQuery = PageQuery.extend({
  q: TextFilter.optional(),
  status: PublishStatus.optional(),
  kind: PlaylistKind.optional(),
});
export type CmsPlaylistsQuery = z.infer<typeof CmsPlaylistsQuery>;
export type CmsPlaylistsQueryInput = z.input<typeof CmsPlaylistsQuery>;

export const CmsPlaylistsResponse = Page(CmsPlaylistDTO);
export type CmsPlaylistsResponse = z.infer<typeof CmsPlaylistsResponse>;

/** `POST /cms/playlists` */
export const CmsPlaylistCreateBody = z.object({
  kind: PlaylistKind.default('editorial'),
  name: I18nText,
  description: z.string().max(1000).nullable().optional(),
  imagePath: z.string().max(512).nullable().optional(),
  artistId: EntityId.nullable().optional(),
  status: PublishStatus.default('draft'),
  /** Initial ordered song ids (optional; `PUT /songs` replaces later). */
  songIds: z.array(EntityId).max(500).optional(),
});
export type CmsPlaylistCreateBody = z.infer<typeof CmsPlaylistCreateBody>;
export type CmsPlaylistCreateBodyInput = z.input<typeof CmsPlaylistCreateBody>;

/** `PATCH /cms/playlists/:id` — at least one key, unknown keys refused. */
export const CmsPlaylistPatchBody = CmsPlaylistCreateBody.omit({ songIds: true })
  .partial()
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Empty patch' });
export type CmsPlaylistPatchBody = z.infer<typeof CmsPlaylistPatchBody>;

export const CmsPlaylistSongDTO = z.object({
  songId: EntityId,
  title: z.string(),
  artistNames: z.string(),
  imageUrl: z.string().nullable(),
  status: SongStatus,
  position: z.number().int().nonnegative(),
});
export type CmsPlaylistSongDTO = z.infer<typeof CmsPlaylistSongDTO>;

export const CmsPlaylistResponse = z.object({
  playlist: CmsPlaylistDTO,
  songs: z.array(CmsPlaylistSongDTO),
});
export type CmsPlaylistResponse = z.infer<typeof CmsPlaylistResponse>;

/** `PUT /cms/playlists/:id/songs` — ordered ids, full replacement. */
export const CmsPlaylistSongsPutBody = z.object({
  songIds: z
    .array(EntityId)
    .max(500)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'Duplicate song ids' }),
});
export type CmsPlaylistSongsPutBody = z.infer<typeof CmsPlaylistSongsPutBody>;
